'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { Search } from 'lucide-react';
import type { PaginatedResponse, BlogSlim } from '@/types';
import { Card } from '@/components/ui/Card';
import { resolveImageUrl } from '@/lib/image';
import { slugifyRouteValue } from '@/lib/slug';

const buildQuery = (params: Record<string, string | number | undefined>) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
            query.set(key, String(value));
        }
    });
    const qs = query.toString();
    return qs ? `?${qs}` : '';
};

export const BlogListView: React.FC<{
    data: PaginatedResponse<BlogSlim>;
    page: number;
    search?: string;
}> = ({ data, page, search }) => {
    const { t, i18n } = useTranslation();
    return (
        <div className="blog-page">
            <div className="container">
                <div className="page-header">
                    <h1>{t('blog.title')}</h1>
                    <p>{t('blog.subtitle')}</p>
                </div>

                <div className="search-section">
                    <form className="input-wrapper" action="/blog" method="get">
                        <label className="input-label" htmlFor="blog-search">
                            {t('common.search')}
                        </label>
                        <div className="input-container">
                            <span className="input-icon">
                                <Search size={20} />
                            </span>
                            <input
                                id="blog-search"
                                className="input input-with-icon"
                                type="text"
                                name="search"
                                placeholder={t('common.searchPlaceholder')}
                                defaultValue={search || ''}
                            />
                        </div>
                    </form>
                </div>

                <div className="blog-grid">
                    {data.items.length === 0 ? (
                        <p>{t('common.noResults')}</p>
                    ) : (
                        data.items.map((blog) => {
                            const title =
                                i18n.language === 'de' && blog.title_de ? blog.title_de : blog.title;
                            const author =
                                i18n.language === 'de' && blog.Author_de ? blog.Author_de : blog.Author;
                            return (
                                <Link key={blog.id} href={`/blog/${slugifyRouteValue(title)}`}>
                                <Card hover className="blog-card">
                                    {blog.image && (
                                        <img
                                            src={resolveImageUrl(blog.image)}
                                            alt={title}
                                            className="blog-image"
                                            loading="lazy"
                                            decoding="async"
                                            width={1200}
                                            height={675}
                                        />
                                    )}
                                    <div className="blog-content">
                                        <h3>{title}</h3>
                                        {author && (
                                            <p className="blog-meta">
                                                {t('blog.author')}: {author}
                                            </p>
                                        )}
                                        {blog.created_at && (
                                            <p className="blog-date">
                                                {new Date(blog.created_at).toLocaleDateString(i18n.language)}
                                            </p>
                                        )}
                                    </div>
                                </Card>
                            </Link>
                            );
                        })
                    )}
                </div>

                {data.meta.pages > 1 && (
                    <div className="pagination">
                        <Link
                            href={`/blog${buildQuery({ page: Math.max(1, page - 1), search })}`}
                            className={`btn btn-outline ${page === 1 ? 'btn-disabled' : ''}`}
                            aria-disabled={page === 1}
                        >
                            {t('common.previous')}
                        </Link>
                        <span>
                            {t('common.page')} {page} {t('common.of')} {data.meta.pages}
                        </span>
                        <Link
                            href={`/blog${buildQuery({ page: Math.min(data.meta.pages, page + 1), search })}`}
                            className={`btn btn-outline ${page === data.meta.pages ? 'btn-disabled' : ''}`}
                            aria-disabled={page === data.meta.pages}
                        >
                            {t('common.next')}
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};
